import { collection, onSnapshot, query, where, type Timestamp } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../lib/firebase';

/**
 * アンケート機能 (REQ-4.2.8〜10)。
 * Phase 2 sprint 1: 公開済み survey 一覧の read-only subscribe のみ。
 * Phase 5 sprint 2 で SurveyForm (10 種の質問種別 + validation + responses 書込) を追加。
 *
 * 旧 `apps/user/src/lib/surveys.ts` + `components/SurveyListModal.tsx` (L3 参照、docs/97 §3)。
 *
 * 旧との差分:
 * - 「自分の回答有無」(`respondedIds`) は Phase 5 sprint 2 で responses 取得時に追加
 * - 10 種の質問種別 (SurveyQuestionType) の型は shared-types/survey.ts (Phase 3 で本格追加)、
 *   Phase 2 では SurveySummary だけで充分
 */

export type SurveyQuestionType =
  | 'short_text'
  | 'long_text'
  | 'radio'
  | 'checkbox'
  | 'select'
  | 'number'
  | 'rating'
  | 'date'
  | 'location'
  | 'rating_scale';

export interface SurveySummary {
  id: string;
  title: string;
  description: string;
  questionCount: number;
  allowEdit: boolean;
  publishedAt: Date | null;
  /** 並び順のキー (publishedAt が無い doc の fallback、旧2 の updatedAt 降順ソート踏襲)。 */
  updatedAt: Date | null;
  archivedAt: Date | null;
}

export type SurveysState =
  | { status: 'loading'; items: []; error: null }
  | { status: 'ready'; items: SurveySummary[]; error: null }
  | { status: 'error'; items: []; error: Error };

export function useSurveys(
  districtId: string | null,
  /**
   * 再試行用 key (Sprint AM、UI2 無限スピナー解消)。
   * 呼出側で setRetryKey(k=>k+1) するたびに subscribe を貼り直す。
   */
  retryKey: number = 0,
): SurveysState {
  const [state, setState] = useState<SurveysState>({
    status: 'loading',
    items: [],
    error: null,
  });

  useEffect(() => {
    // retry 時に必ず loading に戻す
    setState({ status: 'loading', items: [], error: null });
    // 地区フォールバック根絶: districtId 未解決 (null/空) のときは phase2-default の
    // surveys を購読せず loading のまま待機する (呼出側ページが readiness で gate)。
    if (!districtId) return;
    const col = collection(db, 'districts', districtId, 'surveys');
    // 「配信中」の判定は status === 'published' (admin の公開トグルが書く唯一の field)。
    //
    // 【重要】ここを orderBy('publishedAt','desc') にしてはいけない。Firestore の orderBy は
    // 該当 field を持たない doc を結果から除外するが、v2 には publishedAt を書く実装が
    // どこにも無いため、全 doc が常に 0 件になる (prod 逗子 / 高松で再現、2026-09-04)。
    // 旧2 `apps/user/src/lib/surveys.ts::listPublishedSurveys` と同じく where のみで引き、
    // 並び替えは client 側で行う (単一 field の等価条件なので複合 index も不要)。
    const q = query(col, where('status', '==', 'published'));
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const items: SurveySummary[] = snap.docs.map((d) => {
          const data = d.data();
          const toDate = (v: unknown): Date | null =>
            v && typeof (v as Timestamp).toDate === 'function' ? (v as Timestamp).toDate() : null;
          const questions = Array.isArray(data.questions) ? data.questions : [];
          return {
            id: d.id,
            title: (data.title as string) ?? '',
            description: (data.description as string) ?? '',
            questionCount: questions.length,
            allowEdit: Boolean(data.allowEdit),
            publishedAt: toDate(data.publishedAt),
            updatedAt: toDate(data.updatedAt),
            archivedAt: toDate(data.archivedAt),
          };
        });
        // archived は除外し、新しい順 (publishedAt があればそれ、無ければ updatedAt) に並べる
        const sortKey = (s: SurveySummary) =>
          (s.publishedAt ?? s.updatedAt)?.getTime() ?? Number.NEGATIVE_INFINITY;
        setState({
          status: 'ready',
          items: items.filter((s) => !s.archivedAt).sort((a, b) => sortKey(b) - sortKey(a)),
          error: null,
        });
      },
      (error) => setState({ status: 'error', items: [], error }),
    );
    return unsubscribe;
  }, [districtId, retryKey]);

  return state;
}
